import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, Sparkles } from 'lucide-react';

function ExitIntentPopup() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if popup has already been shown this session
    const shownThisSession = sessionStorage.getItem('exitPopupShown');
    if (shownThisSession) {
      setHasShown(true);
      return;
    }

    let exitIntentTriggered = false;

    const handleMouseLeave = (e) => {
      // Check if mouse is moving out from the top of the viewport
      // This indicates user is going to close tab, type in URL bar, or navigate away
      const shouldTrigger = (
        e.clientY <= 10 && // Mouse at or very close to top
        e.relatedTarget === null && // Mouse left the document
        !exitIntentTriggered && 
        !hasShown
      );

      if (shouldTrigger) {
        exitIntentTriggered = true;
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    const handleMouseOut = (e) => {
      // Alternative detection: mouseout event on document
      // Trigger if mouse leaves document area from top
      if (
        e.clientY < 0 && 
        !exitIntentTriggered && 
        !hasShown
      ) {
        exitIntentTriggered = true;
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Add both event listeners for better coverage
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseout', handleMouseOut);

    // Cleanup
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleCTA = () => {
    setIsVisible(false);
    navigate('/pricing');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-60 z-50 backdrop-blur-sm animate-fadeIn"
        onClick={handleClose}
      ></div>

      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-xl w-full pointer-events-auto transform animate-slideUp relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Gradient Header Accent */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-2xl"></div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
            aria-label="Close popup"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-7 pt-8">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            </div>

            {/* Heading */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
              Wait! Don't leave empty-handed.
            </h2>

            {/* Body Text */}
            <p className="text-base text-gray-600 text-center mb-5 leading-relaxed">
              Tired of spending hours preparing lessons, worksheets, and tests? Get everything ready in <span className="font-semibold text-gray-900">minutes — not hours.</span>
            </p>

            {/* Bullet Points */}
            <div className="space-y-2.5 mb-5">
              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-green-500" strokeWidth={2} />
                </div>
                <p className="text-gray-700 text-sm leading-snug">
                  <span className="font-semibold">AI-generated worksheets, quizzes and test papers</span>
                </p>
              </div>

              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-green-500" strokeWidth={2} />
                </div>
                <p className="text-gray-700 text-sm leading-snug">
                  <span className="font-semibold">AI Assessment Hub</span> — upload and mark papers instantly
                </p>
              </div>

              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-green-500" strokeWidth={2} />
                </div>
                <p className="text-gray-700 text-sm leading-snug">
                  <span className="font-semibold">Ready-to-use teaching resources</span>
                </p>
              </div>

              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-green-500" strokeWidth={2} />
                </div>
                <p className="text-gray-700 text-sm leading-snug">
                  <span className="font-semibold">Designed for teachers, by a teacher</span>
                </p>
              </div>
            </div>

            {/* Trial Message */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-center text-blue-900 font-semibold text-base">
                Try it FREE for 3 days. <span className="text-blue-600">No credit card needed.</span>
              </p>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleCTA}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-base py-3 rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] active:scale-95"
            >
              Start My Free Trial
            </button>

            {/* Dismissal Text */}
            <p className="text-center text-xs text-gray-500 mt-3">
              <button onClick={handleClose} className="hover:text-gray-700 transition-colors underline">
                No thanks, I'll keep struggling with lesson prep
              </button>
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </>
  );
}

export default ExitIntentPopup;
